import React from 'react';
import { FaShieldAlt, FaTrash } from 'react-icons/fa';
import { IoMdClose } from 'react-icons/io';
import { MdOutlineAssignment } from 'react-icons/md';
import { formatBetLine } from '../../../helpers/functions.tsx';
import { Odd } from '../../../models/Odd_response_model.tsx';
import '../../../styles/BetSlip.css';

export type BetSlipMode = 'straight' | 'parlay' | 'teaser';
export type BetSlipStakeFocus = 'risk' | 'win';

const TABS: { id: BetSlipMode; label: string }[] = [
    { id: 'straight', label: 'STRAIGHT' },
    { id: 'parlay', label: 'PARLAY' },
];

export function BetSlipChrome({
    pickCount,
    mode,
    onClose,
    onTabChange,
    notice,
    hideTabs,
    picksLabel,
}: {
    pickCount: number;
    mode: BetSlipMode;
    onClose: () => void;
    onTabChange?: (mode: BetSlipMode) => void;
    notice?: string | null;
    hideTabs?: boolean;
    picksLabel?: string;
}) {
    const pickLabel = picksLabel
        ?? (mode === 'straight' && hideTabs
            ? (pickCount === 1 ? '1 STRAIGHT BET' : `${pickCount} STRAIGHT BETS`)
            : (pickCount === 1 ? '1 PICK' : `${pickCount} PICKS`));

    return (
        <div className="bet-slip-chrome">
            <div className="bet-slip-chrome__bar">
                <div className="bet-slip-chrome__brand">
                    <MdOutlineAssignment className="bet-slip-chrome__icon" aria-hidden="true" />
                    <div>
                        <h1 className="bet-slip-chrome__title">BET SLIP</h1>
                        <p className="bet-slip-chrome__picks">{pickLabel}</p>
                    </div>
                </div>
                <button type="button" className="bet-slip-chrome__close" onClick={onClose} aria-label="Close">
                    <IoMdClose size={25} />
                </button>
            </div>
            {!hideTabs && (
            <div className="bet-slip-chrome__tabs" role="tablist" aria-label="Bet type">
                {TABS.map((tab) => (
                    <button
                        key={tab.id}
                        type="button"
                        role="tab"
                        aria-selected={mode === tab.id}
                        className={`bet-slip-chrome__tab${mode === tab.id ? ' is-active' : ''}`}
                        onClick={() => {
                            if (tab.id !== mode) onTabChange?.(tab.id);
                        }}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>
            )}
            {notice ? (
                <div className="bet-slip-chrome__notice" role="alert">
                    {notice}
                </div>
            ) : null}
        </div>
    );
}

export function BetSlipStake({
    focus,
    onFocusChange,
    risk,
    toWin,
    onRiskChange,
    onWinChange,
    showInputs,
    riskReadOnly,
    winReadOnly,
    riskOnly,
    betTotal,
    possibleWin,
    parlayLegs,
    parlayOdds,
    legsKind,
    hideToggle,
    splitTotals,
    riskTotalLabel,
    winTotalLabel,
}: {
    focus: BetSlipStakeFocus;
    onFocusChange: (focus: BetSlipStakeFocus) => void;
    risk: string | number;
    toWin: string | number;
    onRiskChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
    onWinChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
    showInputs: boolean;
    riskReadOnly?: boolean;
    winReadOnly?: boolean;
    riskOnly?: boolean;
    betTotal?: string | number;
    possibleWin?: string | number;
    parlayLegs?: number;
    parlayOdds?: string;
    legsKind?: string;
    hideToggle?: boolean;
    splitTotals?: boolean;
    riskTotalLabel?: string;
    winTotalLabel?: string;
}) {
    const winLocked = riskOnly || winReadOnly;
    const activeFocus = winLocked ? 'risk' : focus;

    return (
        <div className="bet-slip-stake">
            {parlayLegs != null && parlayLegs > 0 && (
                <div className="bet-slip-stake__parlay">
                    <span>{parlayLegs} LEG {legsKind || 'PARLAY'}</span>
                    {parlayOdds ? <span className="bet-slip-stake__odds">{parlayOdds}</span> : null}
                </div>
            )}
            {!riskOnly && !hideToggle && (
            <div className="bet-slip-stake__toggle" role="group" aria-label="Stake mode">
                <button
                    type="button"
                    className={`bet-slip-stake__toggle-btn${activeFocus === 'risk' ? ' is-active' : ''}`}
                    onClick={() => onFocusChange('risk')}
                >
                    RISK
                </button>
                <button
                    type="button"
                    className={`bet-slip-stake__toggle-btn${activeFocus === 'win' ? ' is-active' : ''}`}
                    onClick={() => {
                        if (!winLocked) onFocusChange('win');
                    }}
                    disabled={winLocked}
                >
                    TO WIN
                </button>
            </div>
            )}
            {showInputs && (
                <div className="bet-slip-stake__fields">
                    <label className={`bet-slip-stake__field${activeFocus === 'risk' ? ' is-active' : ''}`}>
                        <span>Risk</span>
                        <span className="bet-slip-stake__input-wrap">
                            <span className="bet-slip-stake__prefix">$</span>
                            <input
                                type="number"
                                inputMode="decimal"
                                min="0"
                                step="any"
                                value={risk}
                                onChange={onRiskChange}
                                readOnly={riskReadOnly}
                                onFocus={() => onFocusChange('risk')}
                            />
                        </span>
                    </label>
                    <label className={`bet-slip-stake__field${!winLocked && activeFocus === 'win' ? ' is-active' : ''}${winLocked ? ' is-readonly' : ''}`}>
                        <span>To Win</span>
                        <span className="bet-slip-stake__input-wrap">
                            <span className="bet-slip-stake__prefix">$</span>
                            <input
                                type="number"
                                inputMode="decimal"
                                min="0"
                                step="any"
                                value={toWin}
                                onChange={winLocked ? undefined : onWinChange}
                                readOnly={winLocked}
                                tabIndex={winLocked ? -1 : undefined}
                            />
                        </span>
                    </label>
                </div>
            )}
            {!riskOnly && (
            <div className={`bet-slip-stake__totals${splitTotals ? ' bet-slip-stake__totals--split' : ''}`}>
                <div>
                    <span>{riskTotalLabel ?? 'BET TOTAL'}</span>
                    <strong>${betTotal}</strong>
                </div>
                <div>
                    <span>{winTotalLabel ?? 'POSSIBLE WIN'}</span>
                    <strong>${possibleWin}</strong>
                </div>
            </div>
            )}
        </div>
    );
}

export function BetSlipActions({
    mode,
    onClear,
    onPlace,
    placeDisabled,
    placeBusy,
    placeBusyLabel = 'Please wait a moment...',
    placeLabel: placeLabelProp,
    showPlaceIcon = true,
    children,
}: {
    mode: BetSlipMode;
    onClear: () => void;
    onPlace: () => void;
    placeDisabled?: boolean;
    placeBusy?: boolean;
    placeBusyLabel?: string;
    placeLabel?: string;
    showPlaceIcon?: boolean;
    children?: React.ReactNode;
}) {
    const placeLabel =
        placeLabelProp
        ?? (mode === 'parlay' ? 'PLACE PARLAY' : mode === 'teaser' ? 'PLACE TEASER' : 'PLACE WAGER');

    return (
        <div className="bet-slip-actions">
            {children ?? (
                <>
                    <button type="button" className="bet-slip-actions__clear" onClick={onClear}>
                        Clear All
                    </button>
                    <button
                        type="button"
                        className="bet-slip-actions__place"
                        onClick={onPlace}
                        disabled={placeDisabled || placeBusy}
                    >
                        {placeBusy ? (
                            placeBusyLabel
                        ) : (
                            <>
                                {showPlaceIcon ? <FaShieldAlt aria-hidden="true" /> : null}
                                {placeLabel}
                            </>
                        )}
                    </button>
                </>
            )}
        </div>
    );
}

export function SlipOddStepper({
    odds,
    value,
    marketName,
    onSelect,
    pointsOnly = false,
    locked = false,
}: {
    odds: Odd[];
    value?: string;
    marketName?: string;
    onSelect: (odd: Odd) => void;
    pointsOnly?: boolean;
    locked?: boolean;
}) {
    const sorted = [...odds].sort((a, b) => Number(a.points ?? 0) - Number(b.points ?? 0));
    const current = sorted.find((item) => item.id === value) ?? sorted[0];

    if (!current) return null;

    const label = (item: Odd) => formatBetLine(item, marketName, pointsOnly);

    if (locked || sorted.length === 1) {
        return (
            <span className="slip-odd-stepper slip-odd-stepper--static">
                {label(current)}
            </span>
        );
    }

    return (
        <select
            className="slip-odd-stepper"
            value={current.id}
            aria-label="Select odds"
            onChange={(event) => {
                const next = sorted.find((item) => item.id === event.target.value);
                if (next) onSelect(next);
            }}
        >
            {sorted.map((item) => (
                <option key={item.id} value={item.id}>
                    {label(item)}
                </option>
            ))}
        </select>
    );
}

export function BetSlipTrashButton({ onClick }: { onClick: () => void }) {
    return (
        <button type="button" className="slip-pick__trash" onClick={onClick} aria-label="Remove pick">
            <FaTrash />
        </button>
    );
}

export function SlipPickStakes({
    stake,
    profit,
    onStakeChange,
    onProfitChange,
    onDelete,
}: {
    stake: string | number;
    profit: string | number;
    onStakeChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    onProfitChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    onDelete: () => void;
}) {
    return (
        <div className="slip-pick__stakes">
            <label className="slip-pick__stake">
                <span>Risk</span>
                <span className="slip-pick__stake-field">
                    <span className="slip-pick__stake-prefix">$</span>
                    <input
                        type="number"
                        inputMode="decimal"
                        min="0"
                        step="any"
                        value={stake || ''}
                        onChange={onStakeChange}
                    />
                </span>
            </label>
            <label className="slip-pick__stake">
                <span>To Win</span>
                <span className="slip-pick__stake-field">
                    <span className="slip-pick__stake-prefix">$</span>
                    <input
                        type="number"
                        inputMode="decimal"
                        min="0"
                        step="any"
                        value={profit || ''}
                        onChange={onProfitChange}
                    />
                </span>
            </label>
            <BetSlipTrashButton onClick={onDelete} />
        </div>
    );
}

export function SlipPickMarket({ label }: { label?: string | null }) {
    const text = (label || "").trim();
    if (!text) return null;
    return <span className="slip-pick__market">{text}</span>;
}
